import axios, { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	TableFilter,
	acceptChangesTabelRows,
	changeStatusForTabelRows,
	deleteManualTabelRows,
	getDeviation,
	loadTabelRows,
} from '~modules/tabel/api';
import Status from '~modules/tabel/interfaces/status';
import { TabelRowChangeStatusDto } from '~modules/tabel/model/tabel-row-change-status.dto';
import { TabelRowDto } from '~modules/tabel/model/tabel-row.dto';
import { toastError, toastSuccess } from '~shared/ui';

import { WS_TIMEOUT_MESSAGE, WsTimeoutError, useTabelRegeneration } from './useRegeneration';

const TABEL_ROWS_CHUNK_LIMIT = 20;

const mergeRows = (prevRows: TabelRowDto[], chunk: TabelRowDto[]) => {
	const existingIds = new Set(prevRows.map(row => row.id));
	const nextRows = chunk.filter(row => !existingIds.has(row.id));
	return [...prevRows, ...nextRows];
};

export function useTabelRows(
	filter: TableFilter,
	onSaved: (id: number) => void,
	onDownloaded: () => void,
	onPosted: (err?: string) => void,
	onReverted: () => void,
	onDeleted: () => void,
) {
	const [list, setList] = useState<TabelRowDto[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [pending, setPending] = useState<boolean>(false);
	const [chunkPage, setChunkPage] = useState(1);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState<boolean>(true);
	const [debouncedEmployeeName, setDebouncedEmployeeName] = useState(filter.employees_name);
	const prevFilterKey = useRef('');
	const listRef = useRef<TabelRowDto[]>([]);
	// Идентификатор последнего запроса: ответы отставших запросов игнорируются
	const requestIdRef = useRef(0);
	const { connect, disconnect, importAndGenerate } = useTabelRegeneration();

	const setRows = useCallback((updater: (prevRows: TabelRowDto[]) => TabelRowDto[]) => {
		listRef.current = updater(listRef.current);
		setList(listRef.current);
	}, []);

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedEmployeeName(filter.employees_name);
		}, 500);
		return () => clearTimeout(timeout);
	}, [filter.employees_name]);

	useEffect(() => {
		return () => {
			disconnect();
		};
	}, [disconnect]);

	const updateLists = useCallback(
		(data: TabelRowDto[]) => {
			setRows(() => data);
		},
		[setRows],
	);

	const acceptFilter = useCallback(
		async (nextFilter: TableFilter, page: number) => {
			const requestId = ++requestIdRef.current;
			setPending(true);
			return loadTabelRows(nextFilter, page, TABEL_ROWS_CHUNK_LIMIT)
				.then(({ data, total: nextTotal }) => {
					if (requestId !== requestIdRef.current) return;

					let loadedCount = data.length;
					let addedCount = data.length;
					if (page > 1) {
						setRows(prevRows => {
							const nextRows = mergeRows(prevRows, data);
							addedCount = nextRows.length - prevRows.length;
							loadedCount = nextRows.length;
							return nextRows;
						});
					} else {
						updateLists(data);
					}
					setTotal(nextTotal);
					// Признак «есть ещё» берём из самого ответа, а не из list.length >= total:
					// сервер может отдать дубли (нестабильная сортировка), и тогда счётчик
					// строк никогда не дотянется до total, а подгрузка зациклится.
					setHasMore(addedCount > 0 && data.length === TABEL_ROWS_CHUNK_LIMIT && loadedCount < nextTotal);
				})
				.catch(err => {
					if (requestId !== requestIdRef.current) return;
					console.error(err);
				})
				.finally(() => {
					if (requestId !== requestIdRef.current) return;
					setPending(false);
					setLoadingMore(false);
				});
		},
		[setRows, updateLists],
	);

	const refresh = useCallback(async () => {
		setChunkPage(1);
		setHasMore(true);
		return acceptFilter(filter, 1);
	}, [acceptFilter, filter]);

	const adjust = useCallback(
		async (changedRow: TabelRowDto) => {
			try {
				await acceptChangesTabelRows([changedRow]);
				const deviation = await getDeviation(changedRow.id);
				onSaved(changedRow.id);
				setRows(state => state.map(row => (row.id === changedRow.id ? { ...changedRow, deviation } : row)));
			} catch (err) {
				console.error(err);
			}
		},
		[onSaved, setRows],
	);

	const del = useCallback(
		async (ids: number[]) => {
			try {
				await deleteManualTabelRows(ids);
				onDeleted();
				refresh();
			} catch (err) {
				if (err instanceof AxiosError) toastError(err.response?.data.message);
				console.error(err);
			}
		},
		[onDeleted, refresh],
	);

	const post = useCallback(
		async (ids: number[]) => {
			try {
				const rows: TabelRowChangeStatusDto[] = ids.map(id => ({
					id,
					status: Status.COMPLETED,
				}));
				await changeStatusForTabelRows(rows, Status.COMPLETED);
			} catch (err) {
				if (axios.isAxiosError(err)) {
					if (err.response?.data?.message) {
						const msgs = err.response?.data?.message;
						if (Array.isArray(msgs)) {
							msgs.map(msg => toastError(msg));
						}
					}
				} else {
					console.error(err);
				}
			}
			onPosted();
			refresh();
		},
		[refresh, onPosted],
	);

	const regenerate = useCallback(async () => {
		setPending(true);
		try {
			connect();
			await importAndGenerate(({ accepted }) => {
				// Импорт уже шел (cron или другой пользователь) - свой не запускаем, ждем чужой
				if (!accepted) toastSuccess('Обновление данных уже выполняется, ожидаем завершения');
			});
			await refresh();
			onDownloaded();
		} catch (err) {
			// Раньше ошибка WS была молчаливой: пользователь видел только остановившийся индикатор
			console.error(err);
			if (err instanceof WsTimeoutError) {
				toastError(WS_TIMEOUT_MESSAGE);
			} else if (axios.isAxiosError(err)) {
				toastError(err.response?.data?.message ?? 'Ошибка обновления данных');
			} else {
				toastError(err instanceof Error ? err.message : 'Ошибка обновления данных');
			}
		} finally {
			setPending(false);
		}
	}, [connect, importAndGenerate, onDownloaded, refresh]);

	const revert = useCallback(
		async (ids: number[]) => {
			try {
				const rows: TabelRowChangeStatusDto[] = ids.map(id => ({
					id,
					status: Status.IN_PROGRESS,
				}));
				await changeStatusForTabelRows(rows, Status.IN_PROGRESS);
				refresh();
				onReverted();
			} catch (err) {
				console.error(err);
			}
		},
		[onReverted, refresh],
	);

	const filterKey = JSON.stringify({
		employees_name: debouncedEmployeeName,
		startDate: filter.startDate?.getTime(),
		endDate: filter.endDate?.getTime(),
		section_id: filter.section_id,
		withoutActualStay: filter.withoutActualStay,
		withoutCompletedRows: filter.withoutCompletedRows,
		work_time_type: filter.work_time_type,
		schedule_name: filter.schedule_name,
		position_id: filter.position_id,
	});

	useEffect(() => {
		const filterChanged = prevFilterKey.current !== filterKey;
		if (filterChanged) {
			prevFilterKey.current = filterKey;
			// Ответы по старому фильтру больше не должны попадать в список
			requestIdRef.current += 1;
			setRows(() => []);
			setLoadingMore(false);
			setHasMore(true);
			if (chunkPage !== 1) {
				setChunkPage(1);
				return;
			}
		}
		acceptFilter(filter, chunkPage);
	}, [filterKey, chunkPage, acceptFilter, setRows]);

	const loadMore = useCallback(() => {
		if (hasMore && !pending && !loadingMore) {
			setLoadingMore(true);
			setChunkPage(prevPage => prevPage + 1);
		}
	}, [hasMore, pending, loadingMore]);

	return {
		pending,
		loadingMore,
		hasMore,
		list,
		total,
		refresh,
		adjust,
		del,
		post,
		revert,
		regenerate,
		loadMore,
	};
}
